// src/components/Prices/index.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../components/hooks/useAuth";
import PriceItem from "./PriceItem";
import AuthModal from "./AuthModal";
import TradeModal from "./TradeModal";
import TradeStatusModal from "./TradeStatusModal";
import TradeExpiredModal from "./TradeExpiredModal";
import { apiUrl } from "../config/api";

const notificationSound = new Audio("/Notif.mp3");

function Prices({ onItemSelect }) {
  const [activeTab, setActiveTab] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);  // ✅ ابتدا false
  const [error, setError] = useState(null);

  // مودال‌ها
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedTradeType, setSelectedTradeType] = useState(null);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingTrade, setPendingTrade] = useState(null);

  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [expiredTradeId, setExpiredTradeId] = useState(null);

  const { isLoggedIn } = useAuth();
  const lastDataRef = useRef(null);
  const lastToastTimeRef = useRef(0);

  // ────────── دریافت قیمت‌ها ──────────
  const fetchPrices = useCallback(async () => {
    try {
      setLoading(true);  // ✅ شروع لودینگ
      
      const response = await fetch(apiUrl("/api/categories"));
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      const data = await response.json();
      const currentDataString = JSON.stringify(data);

      if (currentDataString !== lastDataRef.current) {
        if (lastDataRef.current !== null) {
          const now = Date.now();
          if (now - lastToastTimeRef.current > 5000) {
            notificationSound.currentTime = 0;
            notificationSound.play().catch(() => {});
            toast.info("⚠️ مظنه تغییر کرد!", {
              position: "top-center",
              autoClose: 3000,
              hideProgressBar: true,
            });
            lastToastTimeRef.current = now;
          }
        }

        setCategories(data);
        lastDataRef.current = currentDataString;

        if (!activeTab && data.length > 0) {
          setActiveTab(data[0].name);
        }
      }
    } catch (err) {
      console.error("Error fetching prices:", err);
      setError(err.message);
    } finally {
      setLoading(false);  // ✅ پایان لودینگ
    }
  }, [activeTab]);

  useEffect(() => {
    fetchPrices();
    const intervalId = setInterval(fetchPrices, 2000);
    return () => clearInterval(intervalId);
  }, [fetchPrices]);

  // ────────── کلیک روی آیتم (خرید) ──────────
  const handleItemClickBuy = useCallback((item) => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    setSelectedItem(item);
    setSelectedTradeType('buy');
    setShowTradeModal(true);
    if (onItemSelect) onItemSelect(item);
  }, [isLoggedIn, onItemSelect]);

  // ────────── کلیک روی آیتم (فروش) ──────────
  const handleItemClickSell = useCallback((item) => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    setSelectedItem(item);
    setSelectedTradeType('sell');
    setShowTradeModal(true);
    if (onItemSelect) onItemSelect(item);
  }, [isLoggedIn, onItemSelect]);

  // ────────── تابعی که پس از ثبت موفق معامله ──────────
  const handleTradeSubmitted = (tradeData) => {
    setPendingTrade(tradeData);
    setShowStatusModal(true);
    setShowExpiredModal(false);
  };

  // ────────── تابعی که وقتی تایمر تمام می‌شود ──────────
  const handleTradeTimeout = () => {
    setShowStatusModal(false);
    setExpiredTradeId(pendingTrade ? pendingTrade.tradeId : null);
    setShowExpiredModal(true);
  };

  // ────────── رندر ──────────
  if (error) return <div className="PriceContainer mainPrices">خطا: {error}</div>;

  const activeCategory = categories.find((cat) => cat.name === activeTab);
  const isActiveCategoryTradeEnabled = activeCategory?.tradeEnabled !== false;
  const showTabs = categories.length > 1;

  return (
    <div className="PriceContainer mainPrices">

      {/* مودال احراز هویت */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      {/* مودال معامله */}
      <TradeModal
        isOpen={showTradeModal}
        onClose={() => {
          setShowTradeModal(false);
          setSelectedItem(null);
          setSelectedTradeType(null);
        }}
        item={selectedItem}
        tabName={activeTab}
        mode={selectedTradeType}
        onTradeSubmitted={handleTradeSubmitted}
      />

      {/* مودال وضعیت معامله */}
      <TradeStatusModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setPendingTrade(null);
        }}
        tradeId={pendingTrade ? pendingTrade.tradeId : null}
        tradeDetails={pendingTrade}
        onTimeout={handleTradeTimeout}
      />

      {/* مودال انقضای معامله */}
      <TradeExpiredModal
        isOpen={showExpiredModal}
        onClose={() => {
          setShowExpiredModal(false);
          setExpiredTradeId(null);
        }}
        tradeId={expiredTradeId}
      />

      {/* تب‌ها */}
      {showTabs && (
        <div className="TitleRow">
          {categories.map((cat) => (
            <p
              key={cat.id}
              className={`TitleItem ${activeTab === cat.name ? "active" : ""}`}
              onClick={() => setActiveTab(cat.name)}
            >
              {cat.name}
            </p>
          ))}
        </div>
      )}

      {/* هدر جدول */}
      <nav>
        <p className="Item">{activeTab || "تب را انتخاب کنید"}</p>
        <p className="Item">از ما بخرید</p>
        <p className="Item">به ما بفروشید</p>
      </nav>

      {/* آیتم‌ها */}
      {activeCategory && (
        <div className="PricesCategoryShell">
        <div className={`${activeTab === "آبشده" ? "FildeOne" : "FildeOTwo"} ${isActiveCategoryTradeEnabled ? "" : "PricesCategoryDisabled"}`}>
          {activeCategory.items
            .filter((item) => item.trade)
            .map((item) => (
              <PriceItem
                key={item.id}
                item={item}
                tabName={activeTab}
                onClickBuy={handleItemClickBuy}
                onClickSell={handleItemClickSell}
              />
            ))}
        </div>
        {!isActiveCategoryTradeEnabled && (
          <div className="PricesDisabledOverlay">
            معاملات {activeCategory.name} غیر فعال است
          </div>
        )}
        </div>
      )}
    </div>
  );
}

export default Prices;
